import type { ContactInfo } from '../types/amazon-connect-streams';

function safe<T>(fn: () => T, fallback: T): T {
    try {
        return fn();
    } catch {
        return fallback;
    }
}

function readSegmentValue(segmentAttributes: Record<string, { ValueString?: string }> | null, key: string): string | undefined {
    if (!segmentAttributes) return undefined;
    const entry = segmentAttributes[key];
    const value = entry?.ValueString;
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** Human-readable media path (WebRTC softphone vs PSTN/desk phone). */
export function formatContactMediaLabel(
    contactSubtype: string | undefined,
    isSoftphone: boolean,
): string {
    if (contactSubtype === 'connect:WebRTC' || contactSubtype?.includes('WebRTC')) {
        return 'WebRTC (browser)';
    }
    if (isSoftphone) return 'Browser softphone';
    return 'PSTN / desk phone';
}

/** Human-readable direction + initiation (inbound vs outbound vs callback). */
export function formatContactDirectionLabel(
    direction: string | undefined,
    initiationMethod: string | undefined,
    isInbound: boolean,
): string {
    const dir = (direction || (isInbound ? 'INBOUND' : 'OUTBOUND')).toUpperCase();
    const method = (initiationMethod || '').toUpperCase();

    if (method === 'CALLBACK' || method.includes('CALLBACK')) {
        return dir === 'OUTBOUND' ? 'Outbound callback' : 'Queue callback';
    }
    if (method === 'API') return 'API outbound';
    if (method === 'TRANSFER' || method === 'QUEUE_TRANSFER') return `${dir === 'INBOUND' ? 'Inbound' : 'Outbound'} transfer`;
    if (dir === 'INBOUND') return 'Inbound';
    return 'Outbound';
}

export function enrichContactInfoFromStreams(c: connect.Contact, base: ContactInfo): ContactInfo {
    const segmentAttributes = safe(
        () => (typeof (c as any).getSegmentAttributes === 'function' ? (c as any).getSegmentAttributes() : null),
        null,
    );

    const contactSubtype =
        safe(() => (typeof (c as any).getContactSubtype === 'function' ? (c as any).getContactSubtype() : null), null) ||
        readSegmentValue(segmentAttributes, 'connect:Subtype');

    const direction =
        readSegmentValue(segmentAttributes, 'connect:Direction') ||
        (safe(() => c.isInbound?.(), false) ? 'INBOUND' : 'OUTBOUND');

    const initiationMethod = safe(
        () => (typeof (c as any).getInitiationMethod === 'function' ? (c as any).getInitiationMethod() : null),
        undefined,
    );

    const isSoftphone = safe(() => c.isSoftphoneCall?.(), false);
    const isInbound = safe(() => c.isInbound?.(), direction === 'INBOUND');

    return {
        ...base,
        contactSubtype: contactSubtype || base.contactSubtype,
        direction: direction || base.direction,
        initiationMethod: initiationMethod || base.initiationMethod,
        mediaLabel: formatContactMediaLabel(contactSubtype, isSoftphone),
        directionLabel: formatContactDirectionLabel(direction, initiationMethod, isInbound),
    };
}

export function snapshotContactSessionFlags(c: connect.Contact): {
    hasTwoActiveParticipants: boolean;
    isUnderSupervision: boolean;
} {
    return {
        hasTwoActiveParticipants: safe(
            () => typeof (c as any).hasTwoActiveParticipants === 'function' && !!(c as any).hasTwoActiveParticipants(),
            false,
        ),
        isUnderSupervision: safe(
            () => typeof (c as any).isUnderSupervision === 'function' && !!(c as any).isUnderSupervision(),
            false,
        ),
    };
}
